/**
 * GSAP Animations Module
 *
 * Inspired by Jonite.com animation patterns.
 * Uses ScrollTrigger for scroll-based reveal animations.
 *
 * @package IceBaths
 */

class GSAPAnimations {
  constructor() {
    this.gsap = window.gsap;
    this.ScrollTrigger = window.ScrollTrigger;
    this.initialized = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Initialize all animations
   */
  init() {
    // Check if GSAP is available
    if (!this.gsap || !this.ScrollTrigger) {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    // Respect user's motion preferences
    if (this.reducedMotion) {
      this.showAllElements();
      return;
    }

    // Register ScrollTrigger plugin
    this.gsap.registerPlugin(this.ScrollTrigger);

    // Set default ease
    this.gsap.defaults({
      ease: 'power2.out',
      duration: 0.8,
    });

    // Initialize all animation groups
    this.initHeaderAnimations();
    this.initHeroAnimations();
    this.initBlockAnimations();
    this.initSubnavRentalAnimations();
		this.initSubnavFactoryAnimations();
    this.initProductAnimations();
    this.initCTAAnimations();
    this.initFeaturedSectionsAnimations();
    this.initGeneralAnimations();

    this.initialized = true;
  }

  /**
   * Helper: Filter out null/undefined elements from array
   */
  filterElements(elements) {
    if (!elements) return [];
    if (elements instanceof NodeList || Array.isArray(elements)) {
      return Array.from(elements).filter(Boolean);
    }
    return elements ? [elements] : [];
  }

  /**
   * Helper: Safely set GSAP properties on elements
   */
  safeSet(elements, props) {
    const filtered = this.filterElements(elements);
    if (filtered.length > 0) {
      this.gsap.set(filtered, props);
    }
    return filtered;
  }

  /**
   * Helper: Safely animate elements with GSAP
   */
  safeTo(elements, props, position) {
    const filtered = this.filterElements(elements);
    if (filtered.length > 0) {
      return this.gsap.to(filtered, props);
    }
    return null;
  }

  /**
   * Show all elements immediately for reduced motion preference
   */
  showAllElements() {
    const elements = document.querySelectorAll(
      '[data-animate], .hero-showcase, .featured-products, .content-with-media, ' +
      '.full-width-cta-block, .subnav-rental, .subnav-factory, .pricing-rental-table'
    );
    elements.forEach(el => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /**
   * Header animations - subtle fade in on load
   */
  initHeaderAnimations() {
    const header = document.querySelector('.header');
    if (!header) return;

    const logo = header.querySelector('.logo');
    const navItems = header.querySelectorAll('.primary__menu-container > ul > li');
    const actions = header.querySelector('.header__actions');

    // Build array of elements that exist
    const headerElements = this.filterElements([logo, ...navItems, actions]);
    if (headerElements.length === 0) return;

    // Initial state
    this.safeSet(headerElements, {
      opacity: 0,
      y: -20,
    });

    // Animate header elements on load
    const tl = this.gsap.timeline({ delay: 0.2 });

    if (logo) {
      tl.to(logo, {
        opacity: 1,
        y: 0,
        duration: 0.6,
      });
    }

    if (navItems.length) {
      tl.to(navItems, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.08,
      }, '-=0.3');
    }

    if (actions) {
      tl.to(actions, {
        opacity: 1,
        y: 0,
        duration: 0.4,
      }, '-=0.2');
    }
  }

  /**
   * Hero showcase animations - dramatic entrance
   */
  initHeroAnimations() {
    // Hero Showcase
    const heroShowcase = document.querySelectorAll('.hero-showcase');
    heroShowcase.forEach(hero => {
      const title = hero.querySelector('.hero-showcase__title');
      const businessInfo = hero.querySelector('.hero-showcase__businessinfo');
      const leftImage = hero.querySelector('.hero-showcase__left-landscape-image');
      const leftCta = hero.querySelector('.hero-showcase__left-landscape-cta-button');
      const rightSection = hero.querySelector('.hero-showcase__right-horizontal');

      // Set initial states only for elements that exist
      const textElements = this.filterElements([title, businessInfo]);
      this.safeSet(textElements, {
        opacity: 0,
        y: 60,
      });

      if (leftImage) {
        this.gsap.set(leftImage, {
          opacity: 0,
          scale: 1.05,
          y: 40,
        });
      }

      if (leftCta) {
        this.gsap.set(leftCta, {
          opacity: 0,
          y: 20,
        });
      }

      if (rightSection) {
        this.gsap.set(rightSection, {
          opacity: 0,
          x: 60,
        });
      }

      // Create animation timeline
      const tl = this.gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top 80%',
          end: 'top 20%',
          toggleActions: 'play none none reverse',
        },
      });

      if (title) {
        tl.to(title, {
          opacity: 1,
          y: 0,
          duration: 0.8,
        });
      }

      if (businessInfo) {
        tl.to(businessInfo, {
          opacity: 1,
          y: 0,
          duration: 0.6,
        }, title ? '-=0.5' : '+=0');
      }

      if (leftImage) {
        tl.to(leftImage, {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1,
        }, '-=0.4');
      }

      if (leftCta) {
        tl.to(leftCta, {
          opacity: 1,
          y: 0,
          duration: 0.5,
        }, '-=0.6');
      }

      if (rightSection) {
        tl.to(rightSection, {
          opacity: 1,
          x: 0,
          duration: 0.8,
        }, '-=0.6');
      }
    });

    // Hero Business
    const heroBusiness = document.querySelectorAll('.hero-business');
    heroBusiness.forEach(hero => {
      this.animateBlockEntrance(hero, {
        staggerChildren: true,
        childSelector: '.hero-business__title, .hero-business__description, .hero-business__cta',
      });
    });
  }

  /**
   * Generic block entrance animations
   */
  initBlockAnimations() {
    // Content with Media blocks
    const contentWithMedia = document.querySelectorAll('.content-with-media');
    contentWithMedia.forEach(block => {
      const title = block.querySelector('.content-with-media__title');
      const description = block.querySelector('.content-with-media__description');
      const media = block.querySelector('.content-with-media__media');
      const caption = block.querySelector('.content-with-media__caption');

      const elements = this.filterElements([title, description, media, caption]);
      if (elements.length === 0) return;

      this.gsap.set(elements, {
        opacity: 0,
        y: 80,
      });

      this.gsap.to(elements, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.15,
        scrollTrigger: {
          trigger: block,
          start: 'top 75%',
          toggleActions: 'play none none reverse',
        },
      });
    });

    // Content Grid blocks
    const contentGrids = document.querySelectorAll('.content-grid');
    contentGrids.forEach(grid => {
      const items = grid.querySelectorAll('.content-grid__item');
      this.animateStaggeredEntrance(items, grid);
    });

    // Image Cards Overlay
    // Note: clearProps:'transform' is required after animation so that position:fixed
    // popups inside .image-card are not clipped by the transform stacking context.
    const imageCards = document.querySelectorAll('.image-cards-overlay');
    imageCards.forEach(block => {
      const cards = block.querySelectorAll('.image-card');
      const filtered = this.filterElements(cards);
      if (filtered.length === 0) return;

      this.gsap.set(filtered, { opacity: 0, y: 60, scale: 0.95 });
      this.gsap.to(filtered, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.12,
        ease: 'power2.out',
        onComplete: () => {
          this.gsap.set(filtered, { clearProps: 'transform' });
        },
        scrollTrigger: {
          trigger: block,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    // Image Content blocks
    const imageContent = document.querySelectorAll('.image-content-block');
    imageContent.forEach(block => {
      this.animateBlockEntrance(block, {
        staggerChildren: true,
        childSelector: '.image-content-block__image, .image-content-block__content',
      });
    });

    // FAQ Section
    const faqSections = document.querySelectorAll('.faq-section');
    faqSections.forEach(block => {
      const title = block.querySelector('.faq-section__title');
      const items = block.querySelectorAll('.faq-item');

      if (title) {
        this.gsap.set(title, { opacity: 0, y: 40 });
        this.gsap.to(title, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          scrollTrigger: {
            trigger: block,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      this.animateStaggeredEntrance(items, block, { y: 30 });
    });

    // Mission Vision Statement
    const missionVision = document.querySelectorAll('.mission-vision-statement-block');
    missionVision.forEach(block => {
      this.animateBlockEntrance(block, {
        staggerChildren: true,
        childSelector: '.mission-vision-statement-block__item',
      });
    });

    // Overview Section
    const overviewSections = document.querySelectorAll('.overview-section');
    overviewSections.forEach(block => {
      const image = block.querySelector('.overview-section__image');
      const content = block.querySelector('.overview-section__content');

      if (image) {
        this.gsap.set(image, { opacity: 0, scale: 1.05 });
        this.gsap.to(image, {
          opacity: 1,
          scale: 1,
          duration: 1,
          scrollTrigger: {
            trigger: block,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      if (content) {
        this.gsap.set(content, { opacity: 0, y: 60 });
        this.gsap.to(content, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: block,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Text Feature blocks
    const textFeature = document.querySelectorAll('.text-feature-block');
    textFeature.forEach(block => {
      this.animateBlockEntrance(block, {
        staggerChildren: true,
        childSelector: '.text-feature-block__title, .text-feature-block__description, .text-feature-block__features',
      });
    });

    // Grid Form Section
    const gridForms = document.querySelectorAll('.grid-form-section');
    gridForms.forEach(block => {
      this.animateBlockEntrance(block, {
        staggerChildren: true,
        childSelector: '.grid-form-section__content, .grid-form-section__form',
      });
    });

    // Logo Slider
    const logoSliders = document.querySelectorAll('.logo-slider');
    logoSliders.forEach(block => {
      this.gsap.set(block, { opacity: 0, y: 40 });
      this.gsap.to(block, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        scrollTrigger: {
          trigger: block,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    });

    // Social Follow Section
    const socialFollow = document.querySelectorAll('.social-follow-section');
    socialFollow.forEach(block => {
      const icons = block.querySelectorAll('.social-follow-section__icon, .social-icon');
      this.animateStaggeredEntrance(icons, block, { y: 30, scale: 0.9 });
    });
  }

  /**
   * Subnav Rental animations
   */
  initSubnavRentalAnimations() {
    const subnavs = document.querySelectorAll('.subnav-rental');

    subnavs.forEach(subnav => {
      const title = subnav.querySelector('.subnav-rental__title');
      const navLinks = subnav.querySelectorAll('.subnav-rental__nav a');
      const cta = subnav.querySelector('.subnav-rental__cta');

      // Create timeline
      const tl = this.gsap.timeline({
        scrollTrigger: {
          trigger: subnav,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

      // Set initial states and animate only if elements exist
      if (title) {
        this.gsap.set(title, { opacity: 0, x: -30 });
        tl.to(title, {
          opacity: 1,
          x: 0,
          duration: 0.5,
        });
      }

      if (navLinks.length) {
        this.gsap.set(navLinks, { opacity: 0, y: 20 });
        tl.to(navLinks, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.1,
        }, title ? '-=0.2' : '+=0');

        // Add hover animations for nav links
        navLinks.forEach(link => {
          link.addEventListener('mouseenter', () => {
            this.gsap.to(link, {
              y: -2,
              duration: 0.2,
              ease: 'power2.out',
            });
          });

          link.addEventListener('mouseleave', () => {
            this.gsap.to(link, {
              y: 0,
              duration: 0.2,
              ease: 'power2.out',
            });
          });
        });
      }

      if (cta) {
        this.gsap.set(cta, { opacity: 0, x: 30 });
        tl.to(cta, {
          opacity: 1,
          x: 0,
          duration: 0.5,
        }, '-=0.3');
      }
    });
  }

	/**
   * Subnav Factory animations
   */
  initSubnavFactoryAnimations() {
    const subnavs = document.querySelectorAll('.subnav-factory');

    subnavs.forEach(subnav => {
      const title = subnav.querySelector('.subnav-factory__title');
      const navLinks = subnav.querySelectorAll('.subnav-factory__nav a');

      // Create timeline
      const tl = this.gsap.timeline({
        scrollTrigger: {
          trigger: subnav,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

      // Set initial states and animate only if elements exist
      if (title) {
        this.gsap.set(title, { opacity: 0, x: -30 });
        tl.to(title, {
          opacity: 1,
          x: 0,
          duration: 0.5,
        });
      }

      if (navLinks.length) {
        this.gsap.set(navLinks, { opacity: 0, y: 20 });
        tl.to(navLinks, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.1,
        }, title ? '-=0.2' : '+=0');

        // Add hover animations for nav links
        navLinks.forEach(link => {
          link.addEventListener('mouseenter', () => {
            this.gsap.to(link, {
              y: -2,
              duration: 0.2,
              ease: 'power2.out',
            });
          });

          link.addEventListener('mouseleave', () => {
            this.gsap.to(link, {
              y: 0,
              duration: 0.2,
              ease: 'power2.out',
            });
          });
        });
      }
    });
  }

  /**
   * Product page animations
   */
  initProductAnimations() {
    // Rental wrapper
    const rentalWrapper = document.querySelectorAll('.rental-wrapper');
    rentalWrapper.forEach(wrapper => {
      const gallery = wrapper.querySelector('.product-gallery, .woocommerce-product-gallery');
      const summary = wrapper.querySelector('.summary');

      if (gallery) {
        this.gsap.set(gallery, { opacity: 0, x: -40 });
        this.gsap.to(gallery, {
          opacity: 1,
          x: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: wrapper,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      if (summary) {
        this.gsap.set(summary, { opacity: 0, x: 40 });
        this.gsap.to(summary, {
          opacity: 1,
          x: 0,
          duration: 0.8,
          delay: 0.2,
          scrollTrigger: {
            trigger: wrapper,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Pricing Rental Table
    const pricingTables = document.querySelectorAll('.pricing-rental-table');
    pricingTables.forEach(table => {
      const tabs = table.querySelectorAll('.pricing-rental-table__tab-button');
      const content = table.querySelector('.pricing-rental-table__content');

      if (tabs.length) {
        this.gsap.set(tabs, { opacity: 0, y: 30 });
        this.gsap.to(tabs, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: table,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      if (content) {
        this.gsap.set(content, { opacity: 0, y: 30 });
        this.gsap.to(content, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          delay: 0.3,
          scrollTrigger: {
            trigger: table,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Product block content
    const productBlocks = document.querySelectorAll('.product-block-rental, .product-block-default');
    productBlocks.forEach(block => {
      // Find all block sections inside
      const sections = block.querySelectorAll('section, .wp-block-group');
      sections.forEach(section => {
				// Check if this section contains fixed-position popups (like image-cards-overlay)
  			const hasFixedPopup = section.classList.contains('image-cards-overlay') ||
                          section.querySelector('.image-card__popup');

        this.gsap.set(section, { opacity: 0, y: 50 });
        this.gsap.to(section, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          onComplete: hasFixedPopup ? () => {
            this.gsap.set(section, { clearProps: 'transform' });
          } : undefined,
					scrollTrigger: {
						trigger: section,
						start: 'top 85%',
    				toggleActions: hasFixedPopup ? 'play none none none' : 'play none none reverse',
					},
        });
      });
    });
  }

  /**
   * CTA block animations
   */
  initCTAAnimations() {
    // Full Width CTA
    const fullWidthCTAs = document.querySelectorAll('.full-width-cta-block');
    fullWidthCTAs.forEach(block => {
      const image = block.querySelector('.full-width-cta-block__image');
      const title = block.querySelector('.full-width-cta-block__title');
      const description = block.querySelector('.full-width-cta-block__description');
      const button = block.querySelector('.full-width-cta-block__cta-button');

      // Parallax effect on image
      if (image) {
        this.gsap.set(image, { scale: 1.1 });
        this.gsap.to(image, {
          scale: 1,
          duration: 1.2,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: block,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }

      // Content reveal - only if we have elements
      const contentElements = this.filterElements([title, description, button]);
      if (contentElements.length > 0) {
        this.gsap.set(contentElements, { opacity: 0, y: 40 });
        this.gsap.to(contentElements, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          scrollTrigger: {
            trigger: block,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Purple CTA Band
    const purpleCTAs = document.querySelectorAll('.purple-cta-band');
    purpleCTAs.forEach(block => {
      this.gsap.set(block, { opacity: 0, y: 40 });
      this.gsap.to(block, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        scrollTrigger: {
          trigger: block,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    });
  }

  /**
   * Featured sections animations (products, articles, case studies, ambassador)
   */
  initFeaturedSectionsAnimations() {
    // Featured Products
    const featuredProducts = document.querySelectorAll('.featured-products');
    featuredProducts.forEach(section => {
      const title = section.querySelector('.featured-product-title');
      const items = section.querySelectorAll('.featured-product-item');

      if (title) {
        this.gsap.set(title, { opacity: 0, y: 40 });
        this.gsap.to(title, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      this.animateStaggeredEntrance(items, section, { y: 60, scale: 0.98 });
    });

    // Featured Articles
    const featuredArticles = document.querySelectorAll('.featured-articles');
    featuredArticles.forEach(section => {
      const title = section.querySelector('.featured-articles__title');
			const description = section.querySelector('.featured-articles__description');
      const items = section.querySelectorAll('.featured-articles__item, .article-card');

      if (title) {
        this.gsap.set(title, { opacity: 0, y: 40 });
        this.gsap.to(title, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }

			if (description) {
				this.gsap.set(description, { opacity: 0, y: 30 });
				this.gsap.to(description, {
					opacity: 1,
					y: 0,
					duration: 0.6,
					delay: 0.2,
					scrollTrigger: {
						trigger: section,
						start: 'top 80%',
						toggleActions: 'play none none reverse',
					},
				});
			}

      this.animateStaggeredEntrance(items, section, { y: 50 });
    });

    // Featured Case Studies
    const featuredCaseStudies = document.querySelectorAll('.featured-case-studies');
    featuredCaseStudies.forEach(section => {
      const title = section.querySelector('.featured-case-studies__title');

      if (title) {
        this.gsap.set(title, { opacity: 0, y: 40 });
        this.gsap.to(title, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });

    // Featured Ambassador
    const featuredAmbassador = document.querySelectorAll('.featured-ambassador');
    featuredAmbassador.forEach(section => {
      this.animateBlockEntrance(section, {
        staggerChildren: true,
        childSelector: '.featured-ambassador__image, .featured-ambassador__content, .featured-ambassador__quote',
      });
    });

    // Featured Media
    const featuredMedia = document.querySelectorAll('.featured-media');
    featuredMedia.forEach(section => {
      const media = section.querySelector('.featured-media__video, .featured-media__image, video, img');
      const content = section.querySelector('.featured-media__content');

      if (media) {
        this.gsap.set(media, { opacity: 0, scale: 1.02 });
        this.gsap.to(media, {
          opacity: 1,
          scale: 1,
          duration: 1,
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      if (content) {
        this.gsap.set(content, { opacity: 0, y: 40 });
        this.gsap.to(content, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          delay: 0.3,
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    });
  }

  /**
   * General animations for common elements
   */
  initGeneralAnimations() {
    // Animate product cards on shop page
    const productCards = document.querySelectorAll('.products .product, .woocommerce-loop-product__link');
    productCards.forEach((card, index) => {
      this.gsap.set(card, { opacity: 0, y: 40 });
      this.gsap.to(card, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        delay: index * 0.08,
        scrollTrigger: {
          trigger: card,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
      });
    });

    // Animate horizontal rules/dividers
    const dividers = document.querySelectorAll('hr');
    dividers.forEach(hr => {
      this.gsap.set(hr, { scaleX: 0, transformOrigin: 'left center' });
      this.gsap.to(hr, {
        scaleX: 1,
        duration: 0.8,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: hr,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
      });
    });

    // Animate images with lazy loading class
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    lazyImages.forEach(img => {
      // Only animate if not already in an animated block
      if (!img.closest('[data-animated]')) {
        img.addEventListener('load', () => {
          this.gsap.fromTo(img,
            { opacity: 0 },
            { opacity: 1, duration: 0.5 }
          );
        });
      }
    });
  }

  /**
   * Helper: Animate block entrance with optional staggered children
   */
  animateBlockEntrance(block, options = {}) {
    if (!block) return;

    const {
      staggerChildren = false,
      childSelector = null,
      y = 60,
      duration = 0.7,
      stagger = 0.15,
    } = options;

    if (staggerChildren && childSelector) {
      const children = block.querySelectorAll(childSelector);
      if (children.length) {
        this.gsap.set(children, { opacity: 0, y });
        this.gsap.to(children, {
          opacity: 1,
          y: 0,
          duration,
          stagger,
          scrollTrigger: {
            trigger: block,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    } else {
      this.gsap.set(block, { opacity: 0, y });
      this.gsap.to(block, {
        opacity: 1,
        y: 0,
        duration,
        scrollTrigger: {
          trigger: block,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });
    }

    block.setAttribute('data-animated', 'true');
  }

  /**
   * Helper: Animate staggered entrance for a collection of elements
   */
  animateStaggeredEntrance(elements, trigger, customProps = {}) {
    if (!elements || !elements.length || !trigger) return;

    const filtered = this.filterElements(elements);
    if (filtered.length === 0) return;

    const defaultProps = {
      y: 50,
      scale: 1,
    };

    const props = { ...defaultProps, ...customProps };

    this.gsap.set(filtered, {
      opacity: 0,
      y: props.y,
      scale: props.scale,
    });

    this.gsap.to(filtered, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      stagger: 0.12,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: trigger,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  /**
   * Refresh ScrollTrigger - call after dynamic content loads
   */
  refresh() {
    if (this.ScrollTrigger) {
      this.ScrollTrigger.refresh();
    }
  }

  /**
   * Kill all animations and ScrollTriggers
   */
  destroy() {
    if (this.ScrollTrigger) {
      this.ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    }
  }
}

// Export for module usage
export default GSAPAnimations;

// Also make available globally for non-module scripts
window.GSAPAnimations = GSAPAnimations;
